package com.fantasytenis.web.components.lineup

import androidx.compose.runtime.Composable
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.shared.domain.lineup.entity.LineupSlots
import com.fantasytenis.shared.domain.tennis.entity.TennisPlayerEntity
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Get the background color for a category.
 */
private fun getCategoryColor(category: Category): CSSColorValue = when (category) {
    Category.SERVE -> Colors.Category.Serve
    Category.FOREHAND -> Colors.Category.Forehand
    Category.BACKHAND -> Colors.Category.Backhand
    Category.VOLLEYS -> Colors.Category.Volleys
    Category.FIGHTING -> Colors.Category.Fighting
}

/**
 * Mobile-optimized horizontal carousel for lineup slots.
 *
 * Features:
 * - Horizontal scroll with CSS snap points
 * - Compact slot display (~100px height vs 200px on desktop)
 * - Active/selecting slot highlighted with category color
 * - Progress indicator showing filled/empty slots
 * - Budget display integrated above carousel
 */
@Composable
fun MobileSlotCarousel(
    slots: LineupSlots,
    players: List<TennisPlayerEntity>,
    selectingCategory: Category?,
    totalCost: Int,
    salaryCap: Int,
    remainingBudget: Int,
    onSlotClick: (Category) -> Unit,
    onRemove: (Category) -> Unit
) {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            marginBottom(16.px)
            overflow("hidden")
        }
    }) {
        // Budget bar at top
        MobileBudgetBar(
            totalCost = totalCost,
            salaryCap = salaryCap,
            remainingBudget = remainingBudget
        )

        // Horizontal scrollable carousel
        Div({
            style {
                display(DisplayStyle.Flex)
                overflowX("auto")
                property("-webkit-overflow-scrolling", "touch")
                property("scroll-snap-type", "x mandatory")
                gap(12.px)
                padding(16.px)
                // Hide scrollbar but keep functionality
                property("scrollbar-width", "none")
                property("-ms-overflow-style", "none")
            }
            classes("mobile-slot-carousel")
        }) {
            Category.entries.forEach { category ->
                val playerId = slots.getPlayerForCategory(category)
                val player = playerId?.let { id -> players.find { it.id == id } }
                val isSelecting = selectingCategory == category
                val isFilled = player != null

                CompactCategorySlot(
                    category = category,
                    player = player,
                    isSelecting = isSelecting,
                    onClick = { onSlotClick(category) },
                    onRemove = if (isFilled) { { onRemove(category) } } else null
                )
            }
        }

        // Progress indicator
        SlotProgressIndicator(slots)
    }
}

/**
 * Compact budget display for mobile carousel header.
 */
@Composable
private fun MobileBudgetBar(
    totalCost: Int,
    salaryCap: Int,
    remainingBudget: Int
) {
    val isOverBudget = remainingBudget < 0
    val percentage = ((totalCost.toDouble() / salaryCap) * 100).coerceIn(0.0, 100.0)

    Div({
        style {
            padding(12.px, 16.px)
            backgroundColor(if (isOverBudget) Colors.ErrorLight else Colors.SurfaceVariant)
            property("border-bottom", "1px solid ${if (isOverBudget) Colors.Error else Colors.Border}")
        }
    }) {
        // Labels row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(6.px)
            }
        }) {
            Span({
                style {
                    fontSize(12.px)
                    fontWeight(600)
                    color(if (isOverBudget) Colors.Error else Colors.TextSecondary)
                }
            }) {
                Text(if (isOverBudget) "Over Budget!" else "Salary Cap")
            }
            Span({
                style {
                    fontSize(12.px)
                    fontWeight(600)
                    color(if (isOverBudget) Colors.Error else Colors.Primary)
                }
            }) {
                Text("$${totalCost}K / $${salaryCap}K")
            }
        }

        // Progress bar
        Div({
            style {
                width(100.percent)
                height(6.px)
                backgroundColor(Colors.Progress.Background)
                borderRadius(3.px)
                overflow("hidden")
            }
        }) {
            Div({
                style {
                    width(percentage.percent)
                    height(100.percent)
                    backgroundColor(
                        when {
                            isOverBudget -> Colors.Error
                            percentage > 90 -> Colors.Progress.Warning
                            else -> Colors.Primary
                        }
                    )
                    property("transition", "width 0.3s ease")
                }
            })
        }

        // Remaining budget text
        Div({
            style {
                marginTop(4.px)
                fontSize(11.px)
                color(if (isOverBudget) Colors.Error else Colors.TextSecondary)
                textAlign("right")
            }
        }) {
            Text(
                if (isOverBudget) "$${-remainingBudget}K over"
                else "$${remainingBudget}K remaining"
            )
        }
    }
}

/**
 * Compact slot card for mobile carousel.
 * Height ~100px (vs 200px on desktop).
 */
@Composable
private fun CompactCategorySlot(
    category: Category,
    player: TennisPlayerEntity?,
    isSelecting: Boolean,
    onClick: () -> Unit,
    onRemove: (() -> Unit)?
) {
    val categoryColor = getCategoryColor(category)

    Div({
        style {
            // Fixed width for carousel consistency
            width(120.px)
            minWidth(120.px)
            height(100.px)
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(12.px)
            cursor("pointer")
            property("transition", "all 0.2s ease")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(6.px)
            position(Position.Relative)
            property("scroll-snap-align", "center")
            flexShrink(0)

            if (isSelecting) {
                property("box-shadow", "0 0 0 3px $categoryColor, 0 4px 12px rgba(0, 0, 0, 0.2)")
                property("transform", "scale(1.05)")
            } else {
                property("box-shadow", "0 2px 6px rgba(0, 0, 0, 0.1)")
            }
        }
        onClick { onClick() }
    }) {
        // Remove button (top-right)
        // Size: 28px for better touch target on mobile compact cards
        if (player != null && onRemove != null) {
            Div({
                style {
                    position(Position.Absolute)
                    top(4.px)
                    right(4.px)
                    width(28.px)
                    height(28.px)
                    borderRadius(50.percent)
                    backgroundColor(Colors.ErrorLight)
                    color(Colors.Error)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    fontSize(14.px)
                    fontWeight(700)
                    cursor("pointer")
                    property("z-index", "1")
                }
                onClick { e ->
                    e.stopPropagation()
                    onRemove()
                }
            }) {
                Text("×")
            }
        }

        // Category badge
        Div({
            style {
                backgroundColor(categoryColor)
                color(Colors.White)
                padding(4.px, 10.px)
                borderRadius(12.px)
                fontSize(10.px)
                fontWeight(600)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(category.displayName)
        }

        // Player info or empty state
        if (player != null) {
            // Player name (truncated)
            Div({
                style {
                    fontSize(12.px)
                    fontWeight(600)
                    color(Colors.TextPrimary)
                    property("white-space", "nowrap")
                    property("overflow", "hidden")
                    property("text-overflow", "ellipsis")
                    maxWidth(100.percent)
                    textAlign("center")
                }
            }) {
                // Show short name (last name only if too long)
                val shortName = if (player.displayName.length > 12) {
                    player.lastName ?: player.displayName
                } else {
                    player.displayName
                }
                Text(shortName)
            }

            // Cost
            Div({
                style {
                    fontSize(11.px)
                    color(Colors.TextSecondary)
                }
            }) {
                val cost = player.getCostForCategory(category)
                Text("$${cost}K")
            }
        } else {
            // Empty state
            Div({
                style {
                    fontSize(11.px)
                    color(if (isSelecting) categoryColor else Colors.TextSecondary)
                    fontWeight(if (isSelecting) 500 else 400)
                    textAlign("center")
                }
            }) {
                Text(if (isSelecting) "Select below" else "Tap to select")
            }
        }
    }
}

/**
 * Progress indicator showing which slots are filled.
 */
@Composable
private fun SlotProgressIndicator(slots: LineupSlots) {
    val filledCount = listOf(
        slots.serve,
        slots.forehand,
        slots.backhand,
        slots.volleys,
        slots.fighting
    ).count { it != null }

    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            gap(8.px)
            paddingBottom(12.px)
        }
    }) {
        repeat(5) { index ->
            val isFilled = index < filledCount
            Div({
                style {
                    width(8.px)
                    height(8.px)
                    borderRadius(50.percent)
                    backgroundColor(if (isFilled) Colors.Primary else Colors.Border)
                    property("transition", "background-color 0.2s ease")
                }
            })
        }

        // Text indicator
        Span({
            style {
                marginLeft(8.px)
                fontSize(11.px)
                color(Colors.TextSecondary)
            }
        }) {
            Text("$filledCount/5")
        }
    }
}

